import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Modal, Spin } from "antd";
import { AlertTriangle, EyeOff, KeyRound, ShieldAlert, Smartphone } from "lucide-react";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../../design-system/tokens";
import { fmtDate } from "../../../design-system/tableUi";
import { CopyButton, Note } from "../ssh-proxy/sshProxyUi";
import { passwordErrorText, revealPasswordResult, revealPasswordStart } from "../../../service/sshProxyService";

// Break-glass reveal of one account's password, behind an MFA push to the
// administrator's own phone. Self-contained: give it the account and open it.
//
//   <RevealPasswordModal open endpointId={1} account="alice" hostname="web-01"
//     rerotateMinutes={60} onClose={...} onRevealed={...} />
//
// The password lives only in this component's state: never logged, never put
// in a URL or storage. It is cleared when its countdown ends, on close and on
// unmount; polling stops on close and unmount too.

const POLL_MS = 2500;

const mmss = (ms) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

function SecretBox({ value }) {
  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
        background: AN_COLORS.tint, border: `1px solid ${AN_COLORS.line}`, borderRadius: 8,
      }}
    >
      <code
        style={{
          flex: 1, minWidth: 0, fontFamily: AN_MONO, fontSize: 15, color: AN_COLORS.ink,
          wordBreak: "break-all", userSelect: "all",
        }}
      >
        {value}
      </code>
      <CopyButton text={value} />
    </div>
  );
}

const centred = { display: "grid", justifyItems: "center", gap: 10, padding: "14px 0 6px", textAlign: "center" };

export default function RevealPasswordModal({ open, endpointId, account, hostname, rerotateMinutes, onClose, onRevealed }) {
  // confirm → starting → waiting → approved → hidden; or denied | expired | shown | error
  const [phase, setPhase] = useState("confirm");
  const [error, setError] = useState("");
  const [deadline, setDeadline] = useState(0);
  const [secret, setSecret] = useState(null); // { password, unconfirmed }
  const [showUntil, setShowUntil] = useState(0);
  const [rerotateAt, setRerotateAt] = useState("");
  const [now, setNow] = useState(() => Date.now());

  const runId = useRef(0);
  const pollTimer = useRef(null);
  const onRevealedRef = useRef(onRevealed);
  onRevealedRef.current = onRevealed;

  // Invalidates any reveal in flight: its timer and its pending answers.
  const stopAll = useCallback(() => {
    runId.current += 1;
    clearTimeout(pollTimer.current);
    pollTimer.current = null;
  }, []);

  // A fresh start each time the modal opens (or the account changes); nothing
  // is kept once it closes.
  useEffect(() => {
    stopAll();
    setSecret(null);
    setPhase("confirm");
    setError("");
    setRerotateAt("");
  }, [open, endpointId, account, stopAll]);

  useEffect(() => () => stopAll(), [stopAll]);

  const ticking = open && (phase === "waiting" || phase === "approved");
  useEffect(() => {
    if (!ticking) return undefined;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [ticking]);

  useEffect(() => {
    if (phase === "waiting" && deadline && now >= deadline) {
      stopAll();
      setPhase("expired");
    } else if (phase === "approved" && showUntil && now >= showUntil) {
      setSecret(null);
      setPhase("hidden");
    }
  }, [now, phase, deadline, showUntil, stopAll]);

  const poll = (revealId, id) => {
    pollTimer.current = setTimeout(() => {
      revealPasswordResult({
        revealId,
        onSuccess: (res) => {
          if (id !== runId.current) return;
          switch (res.status) {
            case "pending":
              poll(revealId, id);
              break;
            case "approved":
              stopAll();
              setSecret({ password: res.password || "", unconfirmed: res.unconfirmed || "" });
              setShowUntil(Date.now() + (Number(res.showSeconds) || 60) * 1000);
              setRerotateAt(res.rerotateAt || "");
              setNow(Date.now());
              setPhase("approved");
              onRevealedRef.current && onRevealedRef.current();
              break;
            case "denied":
            case "expired":
            case "shown":
              stopAll();
              setPhase(res.status);
              break;
            default:
              stopAll();
              setError("Unexpected answer from the server.");
              setPhase("error");
          }
        },
        onFailure: (err) => {
          if (id !== runId.current) return;
          stopAll();
          setError(passwordErrorText(err, "Couldn't read the reveal."));
          setPhase("error");
        },
      });
    }, POLL_MS);
  };

  const start = () => {
    stopAll();
    const id = runId.current;
    setError("");
    setPhase("starting");
    revealPasswordStart({
      endpointId: Number(endpointId),
      account,
      onSuccess: (res) => {
        if (id !== runId.current) return;
        if (!res.revealId) {
          setError("The server didn't start the reveal.");
          setPhase("error");
          return;
        }
        setDeadline(Date.now() + (Number(res.waitSeconds) || 300) * 1000);
        setNow(Date.now());
        setPhase("waiting");
        poll(res.revealId, id);
      },
      onFailure: (err) => {
        if (id !== runId.current) return;
        setError(passwordErrorText(err, "Couldn't start the reveal."));
        setPhase("error");
      },
    });
  };

  const close = () => {
    stopAll();
    setSecret(null);
    onClose && onClose();
  };

  const minutes = Number(rerotateMinutes) > 0 ? Number(rerotateMinutes) : 60;
  const who = `${account || "?"}@${hostname || `endpoint #${endpointId}`}`;
  const again = (
    <Button key="again" onClick={() => setPhase("confirm")}>
      New reveal
    </Button>
  );
  const closeBtn = (
    <Button key="close" type={phase === "approved" ? "primary" : "default"} onClick={close}>
      Close
    </Button>
  );

  let body;
  let footer;
  switch (phase) {
    case "confirm":
    case "starting":
      body = (
        <div style={{ fontSize: 13, color: AN_COLORS.ink2, lineHeight: 1.6 }}>
          Reveal the password of <span style={{ fontFamily: AN_MONO, fontWeight: 600 }}>{who}</span>? A push is sent
          to your phone. Use it only at the machine's console. It will be rotated again {minutes} minute
          {minutes === 1 ? "" : "s"} after you see it.
        </div>
      );
      footer = [
        <Button key="cancel" onClick={close}>
          Cancel
        </Button>,
        <Button key="go" type="primary" danger loading={phase === "starting"} icon={<Smartphone size={13} />} onClick={start}>
          Send approval push
        </Button>,
      ];
      break;
    case "waiting":
      body = (
        <div style={centred}>
          <Spin />
          <div style={{ fontSize: 13.5, fontWeight: 500, color: AN_COLORS.ink }}>Approve the push on your phone…</div>
          <div style={{ fontSize: 12.5, color: AN_COLORS.muted }}>
            Waiting for <span style={{ fontFamily: AN_MONO }}>{who}</span> ·{" "}
            <span style={{ fontFamily: AN_MONO }}>{mmss(deadline - now)}</span> left
          </div>
        </div>
      );
      footer = [
        <Button key="cancel" onClick={close}>
          Cancel
        </Button>,
      ];
      break;
    case "approved":
      body = (
        <div style={{ display: "grid", gap: 12 }}>
          {secret?.password && (
            <div style={{ display: "grid", gap: 6 }}>
              <div style={{ fontSize: 11.5, fontWeight: 500, color: AN_COLORS.ink2 }}>
                Password of <span style={{ fontFamily: AN_MONO }}>{who}</span>
              </div>
              <SecretBox value={secret.password} />
            </div>
          )}
          {secret?.unconfirmed && (
            <div style={{ display: "grid", gap: 6 }}>
              <div style={{ fontSize: 11.5, color: AN_COLORS.warn, lineHeight: 1.5 }}>
                Possibly current: the last rotation set this but wasn't confirmed. Try this one if the other is refused.
              </div>
              <SecretBox value={secret.unconfirmed} />
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", fontSize: 12, color: AN_COLORS.muted }}>
            <span>
              Hidden in <span style={{ fontFamily: AN_MONO, color: AN_COLORS.ink2 }}>{mmss(showUntil - now)}</span>
            </span>
            {rerotateAt ? (
              <span>Rotates again at {fmtDate(rerotateAt)}</span>
            ) : (
              <span>No re-rotation scheduled: the account is not under a profile.</span>
            )}
          </div>
        </div>
      );
      footer = [closeBtn];
      break;
    case "hidden":
      body = (
        <div style={centred}>
          <EyeOff size={22} color={AN_COLORS.muted} />
          <div style={{ fontSize: 13, color: AN_COLORS.ink2 }}>Hidden. Start a new reveal if needed.</div>
          {rerotateAt && <div style={{ fontSize: 12, color: AN_COLORS.muted }}>Rotates again at {fmtDate(rerotateAt)}</div>}
        </div>
      );
      footer = [again, closeBtn];
      break;
    case "shown":
      body = (
        <Note tone="info" icon={<EyeOff size={15} />}>
          Already shown once. Start a new reveal if needed.
        </Note>
      );
      footer = [again, closeBtn];
      break;
    case "denied":
      body = (
        <Note tone="danger" icon={<ShieldAlert size={15} />}>
          The push was denied. Nothing was revealed.
        </Note>
      );
      footer = [again, closeBtn];
      break;
    case "expired":
      body = (
        <Note tone="warn" icon={<AlertTriangle size={15} />}>
          The push expired before it was approved. Nothing was revealed.
        </Note>
      );
      footer = [again, closeBtn];
      break;
    default:
      body = (
        <Note tone="danger" icon={<AlertTriangle size={15} />}>
          {error || "Something went wrong."}
        </Note>
      );
      footer = [again, closeBtn];
  }

  return (
    <Modal
      open={open}
      onCancel={close}
      maskClosable={false}
      destroyOnClose
      width={520}
      title={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <KeyRound size={16} color={AN_COLORS.danger} />
          Reveal password · break-glass
        </span>
      }
      footer={footer}
    >
      <div style={{ fontFamily: AN_FONT, paddingTop: 4 }}>{body}</div>
    </Modal>
  );
}

export { RevealPasswordModal };
